import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { SeuilsAlertes, DEFAULT_SEUILS } from '@/types/alertes';
import { invalidateAlertes } from '@/lib/queryInvalidation';

interface UseSeuilsAlertesReturn {
  seuils: SeuilsAlertes;
  isCustom: boolean;
  isLoading: boolean;
  isSaving: boolean;
  saveSeuils: (newSeuils: SeuilsAlertes, custom: boolean) => Promise<void>;
  resetToDefaults: () => Promise<void>;
  refetch: () => Promise<void>;
}

/**
 * Hook pour gérer les seuils d'alertes personnalisables d'une entreprise
 * @param entrepriseId - ID de l'entreprise
 * @returns Seuils, états de chargement, et fonctions de sauvegarde
 */
export const useSeuilsAlertes = (entrepriseId: string | null | undefined): UseSeuilsAlertesReturn => {
  const queryClient = useQueryClient();
  const [seuils, setSeuils] = useState<SeuilsAlertes>(DEFAULT_SEUILS);
  const [isCustom, setIsCustom] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Charger les seuils depuis Supabase
  const loadSeuils = useCallback(async () => {
    if (!entrepriseId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // Récupérer les paramètres de l'entreprise
      const { data: entreprise, error } = await supabase
        .from('entreprises')
        .select('seuils_alertes_custom, seuils_alertes')
        .eq('id', entrepriseId)
        .single();

      if (error) {
        console.error('Erreur chargement seuils:', error);
        // En cas d'erreur, utiliser les défauts
        setSeuils(DEFAULT_SEUILS);
        setIsCustom(false);
        return;
      }

      if (entreprise) {
        const customEnabled = entreprise.seuils_alertes_custom ?? false;
        setIsCustom(customEnabled);

        if (customEnabled && entreprise.seuils_alertes) {
          // Merger les seuils personnalisés avec les défauts (pour garantir toutes les clés)
          const seuilsPerso = entreprise.seuils_alertes as Partial<SeuilsAlertes>;
          setSeuils({ ...DEFAULT_SEUILS, ...seuilsPerso });
        } else {
          setSeuils(DEFAULT_SEUILS);
        }
      }
    } catch (error) {
      console.error('Erreur inattendue chargement seuils:', error);
      setSeuils(DEFAULT_SEUILS);
      setIsCustom(false);
    } finally {
      setIsLoading(false);
    }
  }, [entrepriseId]);

  // Sauvegarder les seuils dans Supabase
  const saveSeuils = useCallback(async (newSeuils: SeuilsAlertes, custom: boolean) => {
    if (!entrepriseId) {
      toast.error('Entreprise non trouvée');
      return;
    }

    try {
      setIsSaving(true);

      const { error } = await supabase
        .from('entreprises')
        .update({
          seuils_alertes_custom: custom,
          seuils_alertes: custom ? newSeuils : null,
        })
        .eq('id', entrepriseId);

      if (error) {
        console.error('Erreur sauvegarde seuils:', error);
        toast.error('Erreur lors de l\'enregistrement des seuils');
        return;
      }

      // Mettre à jour l'état local
      setIsCustom(custom);
      setSeuils(custom ? newSeuils : DEFAULT_SEUILS);
      toast.success('Seuils d\'alertes enregistrés');

      // Invalider les alertes React Query (remplace l'ancien event seuils-alertes-updated)
      if (entrepriseId) invalidateAlertes(queryClient, entrepriseId);
    } catch (error) {
      console.error('Erreur inattendue sauvegarde seuils:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setIsSaving(false);
    }
  }, [entrepriseId]);

  // Réinitialiser aux valeurs par défaut
  const resetToDefaults = useCallback(async () => {
    await saveSeuils(DEFAULT_SEUILS, false);
  }, [saveSeuils]);

  // Charger les seuils au montage et quand entrepriseId change
  useEffect(() => {
    loadSeuils();
  }, [loadSeuils]);

  return {
    seuils,
    isCustom,
    isLoading,
    isSaving,
    saveSeuils,
    resetToDefaults,
    refetch: loadSeuils,
  };
};

export default useSeuilsAlertes;
